import { isNil, isNotNil } from 'ramda';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { AppState } from '../../../Store/rootReducer';
import {
  loadLibrary, setClient, setLocationId, setPreQualificationResult,
  setSelectedLanguageId, setTargetAssignedToIdForNewApplications, submitPreQualification, setCustomerPortalLinkId
} from '../../../Store/Shared/actions';import MessageViewer from '../../Controls/MessageViewer/MessageViewer';
import PreQualificationMessageDialog from '../../Dialogs/PreQualificationMessageDialog/PreQualificationMessageDialog';
import LoadingIndicator from '../../Shared/LoadingIndicator/LoadingIndicator';
import PreQualifyResultSummary from './PreQualifyResultSummary';
import styles from './_pre-qualify-form-page.module.scss';
import { hasValue, languages, LanguageSelector, PreQualifyCustomer, PreQualifyForm, removeSpecialCharactersFromPhoneNumber } from '@amsanalytics/react-shared-library';
import { getPortalPreQualificationResult } from '../../../Store/CustomerPortal/actions';
import { getWordingForLanguage } from '../../../Helpers/LanguageDictionary';
import { PortalPreQualifyInfo } from '../../../Models/PortalPreQualifyInfo';
import { useAppDispatch } from '../../../Store/useAppDispatch';
import { LeadSourceTrackingData } from '../../../Models/LeadSourceParameters';

// Build a success or error message for the pre-qualification summary dialog.
const buildPreQualificationSummaryMessage = (clientName: string): string => {
  return `<p>Thank you for your interest. We received your information and someone from ${clientName} will be in contact with you shortly to work out a loan program.</p>`;
}

export default function PreQualifyFormPage() {
  // So we can get client id from url
  const params = useParams();
  // So we can dispatch actions
  const dispatch = useAppDispatch();
  // Saved pre-qulification credentials
  const preQualificationCredentials = useSelector((state: AppState) => state.shared.preQualificationCredentials);
  // ID of the employee we should assign the contact's opportunity to
  const targetAssignedToIdForNewApplication = useSelector<AppState, string | null>(appState => appState.shared.targetAssignedToIdForNewApplications);
  // Pre-qualification customer state
  const [preQualifyCustomer, setPreQualifyCustomer] = useState<Partial<PreQualifyCustomer>>({});
  // The client id
  const clientId = useSelector<AppState, string | null>(appState => appState.shared.clientId);
  // The client name
  const clientName = useSelector<AppState, string | null>(appState => appState.shared.clientName);
  // Location Id
  const locationId = useSelector<AppState, string | null>(appState => appState.shared.locationId);
  // Whether or not we are pre-quailifying the customer.
  const isPreQualificationInProgress = useSelector<AppState, boolean>(appState => appState.shared.isPreQualificationInProgress);
  // The pre-qualification result. Null indicates that the pre-qualification has not been completed.
  const prequalificationResult = useSelector<AppState, PortalPreQualifyInfo | null>(appState => appState.shared.preQualifyCustomerInfo);
  // Whether or not we are getting portal pre-qualification result info
  const isLoadingPortalPrequalificationResults = useSelector<AppState, boolean>(appState => appState.customerPortal.isLoadingPortalPrequalificationResults);
  // Privacy policy url
  const privacyPolicyUrl = useSelector<AppState, string | null>(appState => appState.shared.privacyPolicyUrl);
  // Terms and Conditions url
  const termsConditionsUrl = useSelector<AppState, string | null>(appState => appState.shared.termsConditionsUrl);
  // Disclosure verbiage
  const disclosure = useSelector((appState: AppState) => appState.shared.preQualifyDisclosure);
  // spanish disclosure verbiage
  const spanishDisclosure = useSelector((appState: AppState) => appState.shared.spanishPreQualifyDisclosure);
  // List of states
  const states = useSelector((appState: AppState) => appState.shared.states);
  // Whether or not to show the monthly income field
  const showPreQualifyIncomeField = useSelector((appState: AppState) => appState.shared.showPreQualifyIncomeField);
  // Whether or not the monthly income field is required
  const isPreQualifyIncomeRequired = useSelector((appState: AppState) => appState.shared.isPreQualifyIncomeRequired);
  // Whether or not the down payment field is required
  const isPreQualifyDownPaymentRequired = useSelector((appState: AppState) => appState.shared.isPreQualifyDownPaymentRequired);
  // Whether or not to show the down payment field
  const showPreQualifyDownPaymentField = useSelector((appState: AppState) => appState.shared.showPreQualifyDownPaymentField);
  // List of state IDs to filter the address state field by
  const applicantAddressStateIdsToFilterBy = useSelector((appState: AppState) => appState.shared.applicantAddressStateIdsToFilterBy);
  // Language translations
  const languageTranslations = useSelector((appState: AppState) => appState.shared.languageTranslations);
  // Whether or not the user can toggle between English and Spanish
  const allowSpanishTranslation = useSelector((appState: AppState) => appState.shared.allowSpanishTranslation);
  // Whether or not the pre-qualification summary dialog is open
  const [preQualificationSummaryDialogOpen, setPreQualificationSummaryDialogOpen] = useState(false);
  // selected language id. 1 = English, 2 = Spanish
  const selectedLanguageId = useSelector((appState: AppState) => appState.shared.selectedLanguageId);
  const portalLinkId = useSelector<AppState, string | null>(appState => appState.shared.customerPortalLinkId);
  const leadSourceTrackingInfo: LeadSourceTrackingData = useSelector((appState: AppState) => appState.shared.leadSourceParameters);

  // User has submitted the Pre-Qualify form
  const submitPreQualificationForm = (customer: PreQualifyCustomer) => {
    // Dispatch action to submit form. Ensure a valid client id is set and uppercase
    if (!isNil(clientId)) {
      dispatch(submitPreQualification(
        {
          ...customer,
          clientId: clientId?.toUpperCase(),
          homePhone: customer.homePhone ? removeSpecialCharactersFromPhoneNumber(customer.homePhone) : undefined,
          cellPhone: removeSpecialCharactersFromPhoneNumber(customer.cellPhone),
          customerPortalLinkId: portalLinkId,
          targetAssignedToId: targetAssignedToIdForNewApplication,
          trackingId: hasValue(leadSourceTrackingInfo.trackingData?.trackingId) 
            ? leadSourceTrackingInfo.trackingData.trackingId 
            : undefined, // ID reference to tracking data stored in S3
        },
        locationId))
    }
  }

  // returns true if we should show the pre-qualification summary dialog, false otherwise.
  const shouldShowPreQualificationSummaryDialog = (): boolean => {
    return !isNil(prequalificationResult) && (prequalificationResult.success !== true || prequalificationResult.creditHit !== true);
  };

  useEffect(() => {
    const clientId = (params as any)?.clientId;
    const amsCode = (params as any)?.amsCode;
    const customerPortalLinkId = (params as any)?.customerPortalLinkId;
    const salesPersonId = (params as any)?.salesPersonId;
    if (clientId) {
      amsCode ? dispatch(loadLibrary(clientId, amsCode)) : dispatch(loadLibrary(clientId));
      dispatch(setClient(clientId));
    }
    if (amsCode) {
      dispatch(setLocationId(amsCode));
    }
    if (customerPortalLinkId) {
      dispatch(setCustomerPortalLinkId(customerPortalLinkId));
      dispatch(getPortalPreQualificationResult(customerPortalLinkId));
    }
    // Get the salesPersonId if it is present in the url parameters.
    if (salesPersonId) {
      dispatch(setTargetAssignedToIdForNewApplications(salesPersonId));
    }
  }, [params]);

  // After customer submits the form, redirect the customer or show the pre-qualification summary dialog.
  useEffect(() => {
    // if redirectUrl exists on the prequalificationResult, redirect to that url
    if (prequalificationResult && hasValue(prequalificationResult.redirectUrl)) {
      window.location.href = prequalificationResult.redirectUrl!;
    } else {
      if (prequalificationResult?.customerInfo) {
        setPreQualifyCustomer({
          firstName: prequalificationResult.customerInfo.firstName,
          middleInitial: prequalificationResult.customerInfo.middleName ? prequalificationResult.customerInfo.middleName[0] : '',
          lastName: prequalificationResult.customerInfo.lastName,
          suffix: prequalificationResult.customerInfo.suffix,
          address: prequalificationResult.customerInfo.address,
          zipCode: prequalificationResult.customerInfo.zipCode,
          city: prequalificationResult.customerInfo.city,
          stateAbbreviation: prequalificationResult.customerInfo.stateAbbreviation,
          homePhone: prequalificationResult.customerInfo.homePhone,
          cellPhone: prequalificationResult.customerInfo.cellPhone,
          email: prequalificationResult.customerInfo.email,
        });
      } else {
        // Show the pre-qualification summary dialog if needed.
        setPreQualificationSummaryDialogOpen(shouldShowPreQualificationSummaryDialog() === true);
      }
    }
  }, [prequalificationResult]);

  // Update pre-qualification customer state when the form is submitted.
  useEffect(() => {
    if (isNotNil(preQualificationCredentials)) {
      setPreQualifyCustomer(preQualificationCredentials);
    }
  }, [preQualificationCredentials]);

  // returns true if we should show the pre-qualification summary page, false otherwise. if we are redirecting, we should not show the summary page.
  const shouldShowPreQualificationSummary = (): boolean => {
    return isNotNil(prequalificationResult) && isNil(prequalificationResult.redirectUrl) && prequalificationResult.success === true && prequalificationResult.creditHit === true;
  };

  // Get the loading indicator message based on the selected language.
  const getLoadingIndicatorMessage = (): string => {
    return selectedLanguageId === '1'
      ? 'Pre-Qualification in progress. You may be redirected soon. Just a moment...'
      : 'Pre-cualificación en progreso. Puede ser redirigido pronto. Solo un momento';
  }

  return (
    <>
      {isPreQualificationInProgress === true ? <LoadingIndicator label={getLoadingIndicatorMessage()} />
        : isLoadingPortalPrequalificationResults === true ? <LoadingIndicator label={`${getWordingForLanguage('loadingprequalification', selectedLanguageId)}...`} />
          : shouldShowPreQualificationSummary() === true ?
            <div className={styles.preQualifyFormContainer}>
              {allowSpanishTranslation && (
                <LanguageSelector
                  selectedLanguageId={selectedLanguageId}
                  languages={languages}
                  style={{ width: 'fit-content' }}
                  label={selectedLanguageId === '1' ? 'Preferred Language' : 'Idioma Preferido'}
                  onSelectLanguage={(languageId: string) => {
                    // Set the document title based on the selected language
                    document.title = languageId === '1' ? 'Customer Credit Application' : 'Solicitud de Crédito del Cliente';
                    dispatch(setSelectedLanguageId(languageId));
                  }}
                />
              )}
              <PreQualifyResultSummary
                customerName={`${preQualifyCustomer?.firstName || ''} ${preQualifyCustomer?.lastName || ''}`}
                clientName={clientName || ''}
                preQualifyResult={prequalificationResult!}
                selectedLanguageId={selectedLanguageId}
                languageTranslations={languageTranslations}
                creditAppUrl={prequalificationResult?.creditApplicationUrl}
                onRetry={() => {
                  // clear out the pre-qualification result because the user is retrying
                  dispatch(setPreQualificationResult(null));
                  // clear out the pre-qualification credentials because the user is retrying
                  setPreQualifyCustomer({});
                }}
              />
            </div>
            : <div className={styles.preQualifyFormContainer}>
              <div className={styles.innerContainer}>
                {allowSpanishTranslation && (
                  <LanguageSelector
                    selectedLanguageId={selectedLanguageId}
                    languages={languages}
                    style={{ width: 'fit-content' }}
                    label={selectedLanguageId === '1' ? 'Preferred Language' : 'Idioma Preferido'}
                    onSelectLanguage={(languageId: string) => {
                      // Set the document title based on the selected language
                      document.title = languageId === '1' ? 'Customer Credit Application' : 'Solicitud de Crédito del Cliente';
                      dispatch(setSelectedLanguageId(languageId));
                    }}
                  />
                )}
                <PreQualifyForm
                  customer={preQualifyCustomer}
                  disclosure={selectedLanguageId === '2' ? (spanishDisclosure || disclosure || '') : (disclosure || '')}
                  states={states}
                  stateIdsToFilterBy={applicantAddressStateIdsToFilterBy}
                  privacyNoticeUrl={!isNil(privacyPolicyUrl) ? privacyPolicyUrl : '/'}
                  termsAndConditionsUrl={!isNil(termsConditionsUrl) ? termsConditionsUrl : '/'}
                  showMonthlyIncomeField={showPreQualifyIncomeField || isPreQualifyIncomeRequired}
                  isMonthlyIncomeFieldRequired={isPreQualifyIncomeRequired}
                  showDownPaymentField={showPreQualifyDownPaymentField || isPreQualifyDownPaymentRequired}
                  isDownPaymentFieldRequired={isPreQualifyDownPaymentRequired}
                  onChange={(customer: Partial<PreQualifyCustomer>) => setPreQualifyCustomer(customer)}
                  selectedLanguageId={selectedLanguageId}
                  languageTranslations={languageTranslations}
                  onSubmit={() => {
                    submitPreQualificationForm(preQualifyCustomer as PreQualifyCustomer);
                  }}
                />
              </div>
              <PreQualificationMessageDialog
                title='Pre-Qualification Summary'
                isSuccess={prequalificationResult?.success || false}
                wasCreditHit={prequalificationResult?.creditHit || false}
                message={prequalificationResult?.success === true ? buildPreQualificationSummaryMessage(clientName || '') : prequalificationResult?.description || ''}
                open={preQualificationSummaryDialogOpen}
                accept={() => setPreQualificationSummaryDialogOpen(false)}
                closeDialog={() => setPreQualificationSummaryDialogOpen(false)}
              />
            </div>
      }
      <MessageViewer />
    </>
  );
}
